/**
 * ConfirmDialog — the confirmation step in front of a destructive action that
 * does NOT warrant a typed phrase.
 *
 * WHY: logging out is not catastrophic, but it is a hard stop for someone
 * mid-way through a renovation question on a phone, and the control sits three
 * rows below "Download my data" in a scrolling list. A confirmation costs one
 * tap and removes an entire class of "I didn't mean to press that". Account
 * DELETION keeps its own stronger gate (a typed phrase) in
 * `DeleteAccountDialog` — this is deliberately the lighter of the two.
 *
 * The dialog names the consequence rather than asking "Are you sure?", because
 * "are you sure" tells an anxious first-time user nothing about what they are
 * agreeing to.
 */
import { type ReactElement } from "react";

import { Button } from "@garden/ui";

import { DialogShell } from "@/features/account/components/dialog";

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  /** Runs on confirm. The caller closes/navigates. */
  onConfirm: () => void;
  title: string;
  /** What will actually happen, in one sentence. */
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  /** Accessible label for the header close control. */
  closeLabel: string;
  /** Shown on the confirm button while the action runs. */
  busyLabel?: string;
  busy?: boolean;
  tone?: "default" | "danger";
  testId?: string;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel,
  cancelLabel,
  closeLabel,
  busyLabel,
  busy = false,
  tone = "danger",
  testId = "confirm-dialog",
}: ConfirmDialogProps): ReactElement | null {
  return (
    <DialogShell
      open={open}
      onClose={onClose}
      title={title}
      tone={tone}
      closeLabel={closeLabel}
      testId={testId}
      backdropTestId={`${testId}-backdrop`}
      closeTestId={`${testId}-close`}
      titleId={`${testId}-title`}
      footer={
        <>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={busy}
            data-testid={`${testId}-cancel`}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={tone === "danger" ? "destructive" : "primary"}
            loading={busy}
            onClick={onConfirm}
            data-testid={`${testId}-confirm`}
          >
            {busy && busyLabel != null ? busyLabel : confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[14px] leading-[1.55] text-[var(--g-ink-2)]">{body}</p>
    </DialogShell>
  );
}

export default ConfirmDialog;
